import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../assets/carebeyondsidebar.png';
import BookDemoModal from './BookDemoModal';

const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'For Doctors', href: '/for-doctors' },
  { label: 'Security', href: '/security' },
  { label: 'Contact', href: '/contact' }
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href === '/') {
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
    } else if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/', { state: { scrollTo: href } });
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#F8F9FFCC',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          top: 0
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            maxWidth: '1280px',
            mx: 'auto',
            px: { xs: 2.5, sm: 4, md: 6 },
            height: { xs: '64px', md: '80px' }
          }}
        >
          {/* Logo Brand on Left */}
          <Box
            onClick={() => handleNavClick('#home')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.02)' }
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Care Beyond Logo"
              sx={{
                height: { xs: '32px', md: '38px' },
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: { xs: '8px', md: '9.5px' },
                fontWeight: 600,
                color: '#64748b',
                letterSpacing: '0.2px',
                mt: 0.2,
                whiteSpace: 'nowrap'
              }}
            >
              By VDepth Technologies Private Limited
            </Typography>
          </Box>

          {/* Desktop Navigation Links */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: { md: 2.5, lg: 4 }
            }}
          >
            {NAV_ITEMS.map((item) => (
              <Box
                key={item.label}
                onClick={() => handleNavClick(item.href)}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  py: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#475569',
                  letterSpacing: '-0.1px',
                  transition: 'color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    color: '#2563eb'
                  },
                  // Animated Underline
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '0%',
                    width: '100%',
                    height: '2px',
                    backgroundColor: '#2563eb',
                    transform: 'scaleX(0)',
                    transformOrigin: 'center',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                  },
                  '&:hover::after': {
                    transform: 'scaleX(1)'
                  }
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>

          {/* Right Side CTAs */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => setDemoOpen(true)}
              sx={{
                backgroundColor: '#2563eb',
                backgroundImage: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                color: '#ffffff',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
                fontSize: '15px',
                textTransform: 'none',
                px: 3,
                py: 1.2,
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Book a Demo
            </Button>
          </Box>

          {/* Mobile Drawer Trigger (Always loaded in DOM, conditionally shown by CSS breakpoints) */}
          <IconButton
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: 'block', md: 'none' },
              color: '#475569',
              mr: 2.5
            }}
          >
            <MenuIcon sx={{ fontSize: '28px' }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Navigation */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        transitionDuration={{ enter: 350, exit: 250 }}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: '250px',
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            p: 2.5
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#475569', mr: -1.5 }}>
            <CloseIcon sx={{ fontSize: '26px' }} />
          </IconButton>
        </Box>

        <List sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 0 }}>
          {NAV_ITEMS.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(item.href)}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.04)',
                    color: '#2563eb'
                  }
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#334155'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setMobileOpen(false);
              setDemoOpen(true);
            }}
            sx={{
              backgroundColor: '#2563eb',
              backgroundImage: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: '#ffffff',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: '15px',
              textTransform: 'none',
              py: 1.5,
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(37, 99, 235, 0.3)'
              }
            }}
          >
            Book a Demo
          </Button>
        </Box>
      </Drawer>

      {/* Interactive Modal */}
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
